import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Bookmark, Play, Trash2, Lightbulb } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface KnowledgeCard {
  storyId: string;
  title: string;
  category: string;
  difficulty: string;
  surface: string;
  answer: string;
  truth: string;
  hints: string[];
  tags: string[];
  savedAt: string;
}

const KnowledgeCardsPage: React.FC = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<KnowledgeCard[]>([]);

  useEffect(() => {
    try {
      setCards(JSON.parse(localStorage.getItem('lts_knowledge_cards') || '[]'));
    } catch {
      setCards([]);
    }
  }, []);

  const handleRemove = (storyId: string) => {
    if (!window.confirm('确定要删除这张知识卡片吗？')) return;
    const next = cards.filter(c => c.storyId !== storyId);
    localStorage.setItem('lts_knowledge_cards', JSON.stringify(next));
    setCards(next);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white">
      <header className="bg-white shadow-sm border-b border-gray-200 px-4 py-3">
        <div className="flex justify-between items-center max-w-6xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 transition"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回主页
          </button>
          <h1 className="text-xl font-bold flex items-center text-emerald-700">
            <Bookmark className="w-6 h-6 mr-2" />
            我的知识卡片
          </h1>
          <span className="text-sm text-gray-500">{cards.length} 张</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {cards.length === 0 ? (
          <div className="text-center py-20">
            <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">还没有收藏任何知识卡片</p>
            <p className="text-gray-400 text-sm mb-6">
              完成一局海龟汤推理后，在胜利页面点击「收藏本题知识卡片」即可收集
            </p>
            <button
              onClick={() => navigate('/game')}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition inline-flex items-center"
            >
              <Play className="w-4 h-4 mr-2" />
              去完成一局推理
            </button>
          </div>
        ) : (
          <div className="space-y-5">
            {cards.map((card, index) => (
              <motion.div
                key={card.storyId}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-md border border-gray-200 p-6"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        {card.category}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {card.difficulty === 'easy' ? '简单' : card.difficulty === 'medium' ? '中等' : '困难'}
                      </span>
                      {card.tags.map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-600">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">{card.title}</h3>
                  </div>
                  <button
                    onClick={() => handleRemove(card.storyId)}
                    className="p-2 text-gray-400 hover:text-red-500 transition"
                    title="删除卡片"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-gray-500 mb-1">汤面</p>
                  <p className="text-sm text-gray-700">{card.surface}</p>
                </div>

                <div className="bg-emerald-50 rounded-lg p-3 mb-3">
                  <p className="text-sm text-emerald-600 mb-1 font-medium">汤底（真相）</p>
                  <p className="text-sm text-gray-700">{card.answer}</p>
                  {card.truth && card.truth !== card.answer && (
                    <p className="text-sm text-gray-600 mt-2">{card.truth}</p>
                  )}
                </div>

                {card.hints && card.hints.length > 0 && (
                  <div className="bg-indigo-50 rounded-lg p-3 mb-3">
                    <p className="text-sm text-indigo-600 mb-1 font-medium flex items-center">
                      <Lightbulb className="w-4 h-4 mr-1" />
                      防骗知识点
                    </p>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5">
                      {card.hints.map((hint, i) => (
                        <li key={i}>{hint}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">
                    收藏于 {new Date(card.savedAt).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => navigate(`/game?story=${card.storyId}`)}
                    className="text-sm text-emerald-600 hover:text-emerald-700 font-medium inline-flex items-center"
                  >
                    <Play className="w-3.5 h-3.5 mr-1" />
                    再玩一次
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default KnowledgeCardsPage;
